import {
  Box,
  Button,
  Stack,
  SxProps,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useRef, useMemo, useState, useEffect } from "react";
import { DataTable } from "@components/data-table";
import { LoadingTableBlock } from "@components/data-table/loading-table-block";
import { TableDesktopFooterMenu } from "@components/data-table/data-table-desktop-footer-menu";
import { DataTableDesktopHeaderMenu } from "@components/data-table/data-table-desktop-header-menu";
import { TableSearchInput } from "@components/data-table/search-input";
import { TableSettingsMenu } from "@components/data-table/settings-menu";
import { TableFilterInput } from "@components/data-table/filter-input";
import { TableEditItemsPopover } from "@components/data-table/edit-items-popover";
import { TableMobileMenu } from "@components/data-table/mobile-menu";
import { TableCellInputType } from "@components/data-table/types";

import { useUserTableContext, useUserTableDispatch } from "@hooks/use-table-context/user";
import { useUserConfigContext } from "@hooks/use-config-context/user";
import { useTableAnchorsState } from "@hooks/use-table-anchors-state";
import { ID_KEY, PER_PAGE_OPTIONS } from "@hooks/use-table-context/utils";
import { useCapabilities } from "@hooks/use-capabilities";
import { dataTableResponsiveWidth } from "@components/data-table/utils";
import { getOptionsFromSelectFieldConfig } from "@hooks/use-config/utils";
import type { UserTableFilter } from "@hooks/use-table-context/user/types";
import { underscoreToTitleCase } from "@utils/index";

import { getUserTableData } from "../utils";
import { formatUserForSave } from "@utils/format-user-for-save";

export const UsersTableComponent = ({
  sx,
  hideAddUser = false,
  openUserModal,
}: {
  sx?: SxProps;
  hideAddUser?: boolean;
  openUserModal?: (data?: any) => void;
}) => {
  const tableStackRef = useRef<HTMLDivElement>(null);
  const mobileAnchorRef = useRef<HTMLDivElement>(null);

  const {
    tableData,
    loading,
    hiddenColumns,
    disabledRows,
    page,
    perPage,
    total,
    selectedItems,
    filters,
  } = useUserTableContext();

  const {
    selectItem,
    setPage,
    setPerPage,
    addHiddenColumn,
    removeHiddenColumn,
    addFilter,
    removeFilter,
    removeAllFilters,
    editItems,
  } = useUserTableDispatch();

  const {
    searchMenuAnchor,
    setSearchMenuAnchor,
    filterMenuAnchor,
    setFilterMenuAnchor,
    editUsersAnchor,
    setEditUsersAnchor,
    settingsAnchor,
    setSettingsAnchor,
  } = useTableAnchorsState();

  const [filtersToRemove, setFiltersToRemove] = useState<UserTableFilter[]>([]);

  const { can } = useCapabilities();
  const canCreate = can("create_users");
  const canEdit = can("edit_users");

  const [shouldShowDesktopFooter, setShouldShowDesktopFooter] = useState(
    window.innerWidth >= 900
  );

  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 900;
      if (shouldShowDesktopFooter !== isDesktop) setShouldShowDesktopFooter(isDesktop);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [shouldShowDesktopFooter]);

  const { tableColumns, config, loading: configLoading } = useUserConfigContext();

  const activeTableColumns = useMemo(() => {
    const cols = tableColumns.filter((col) => !hiddenColumns.includes(col.id));
    // Promote user_email to first when student_id is absent
    if (!cols.find((c) => c.id === "student_id")) {
      const emailIdx = cols.findIndex((c) => c.id === "user_email");
      if (emailIdx > 0) {
        const [emailCol] = cols.splice(emailIdx, 1);
        cols.unshift(emailCol);
      }
    }
    return cols;
  }, [tableColumns, hiddenColumns]);

  const activeTableLabels = useMemo(
    () =>
      tableColumns.reduce(
        (acc, col) => ({ ...acc, [col.id]: col.label }),
        {} as Record<string, string>
      ),
    [tableColumns]
  );

  const tableRows = useMemo(
    () => getUserTableData(tableData, activeTableColumns),
    [tableData, activeTableColumns]
  );

  const formatters = useMemo(() => {
    const base: Record<string, (val: any, rowData: any) => any> = {};
    // Select-backed fields (Role, Enrollment Status, etc.) store raw slugs
    // (e.g. "it_local_administrator") — prefer the configured option label,
    // falling back to an auto title-cased (with acronym handling) version
    // so any current/future select field reads properly without per-value config.
    activeTableColumns.forEach((column) => {
      if (column.inputType !== TableCellInputType.Select) return;
      base[column.id] = (val: any) => {
        if (val === "" || val === null || val === undefined) return val;
        return column.options?.[String(val)] ?? underscoreToTitleCase(String(val));
      };
    });
    const nameLinkColId = (
      activeTableColumns.find((c) => c.id === "student_id") ??
      activeTableColumns.find((c) => c.id === "user_email")
    )?.id;
    if (nameLinkColId) {
      base[nameLinkColId] = (val: any, rowData: any) => (
        <span
          className="link"
          role="button"
          tabIndex={0}
          onClick={() => openUserModal?.(rowData)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              openUserModal?.(rowData);
            }
          }}
        >
          {val}
        </span>
      );
    }
    return base;
  }, [activeTableColumns, openUserModal]);

  return (
    <Box sx={{ width: "100%", position: "relative", ...sx }}>
      <Stack className="user-table-stack table-stack" ref={tableStackRef}>
        <DataTableDesktopHeaderMenu
          setSearchAnchor={setSearchMenuAnchor}
          setFilterAnchor={setFilterMenuAnchor}
          setSettingsAnchor={setSettingsAnchor}
          useTableContext={useUserTableContext}
          openScanner={() => {}}
        />

        <div className="controls-bar mobile top" />
        <div id="user-table-mobile-anchor" ref={mobileAnchorRef} />

        {(!tableData.length || activeTableColumns.length === 0) && (loading || configLoading) && (
          <LoadingTableBlock type="Users" />
        )}

        {tableRows.length > 0 && activeTableColumns.length > 0 && (
          <DataTable
            sx={{ width: dataTableResponsiveWidth, m: "0 auto" }}
            data={tableRows}
            getKey={(row) => String(row.wp_user_id ?? row.user_email ?? row.id)}
            labels={activeTableLabels}
            formatCell={formatters}
            onEdit={
              canEdit
                ? () => setEditUsersAnchor(tableStackRef.current)
                : undefined
            }
            bottomWidget={
              canCreate &&
              !hideAddUser &&
              shouldShowDesktopFooter && (
                <TableDesktopFooterMenu
                  openModal={openUserModal}
                  tableContext={useUserTableContext}
                  buttonLabel="Add New User"
                />
              )
            }
            disabledRows={disabledRows}
            onSelect={selectItem}
            onDeselect={selectItem}
            selectedRows={selectedItems}
            page={page}
            perPage={perPage}
            perPageOptions={PER_PAGE_OPTIONS}
            onPageChange={setPage}
            onPerPageChange={setPerPage}
            total={total}
            hiddenColumns={[...(hiddenColumns ?? []), ID_KEY]}
          />
        )}

        {tableRows.length === 0 && !loading && !configLoading && activeTableColumns.length > 0 && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            No users found.
            {filters.length > 0 && (
              <Box sx={{ mt: 2 }}>Try adjusting filters to see more results.</Box>
            )}
            {canCreate && !hideAddUser && shouldShowDesktopFooter && (
              <Box sx={{ mt: 3 }}>
                <Button
                  color="success"
                  onClick={openUserModal}
                  startIcon={<AddIcon />}
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  Add New User
                </Button>
              </Box>
            )}
          </Box>
        )}

        <TableMobileMenu
          setSearchAnchor={() =>
            mobileAnchorRef.current && setSearchMenuAnchor(mobileAnchorRef.current)
          }
          setFilterAnchor={() =>
            mobileAnchorRef.current && setFilterMenuAnchor(mobileAnchorRef.current)
          }
          openModal={canCreate ? openUserModal : undefined}
          newLabel="Add User"
          filters={filters}
        />

        <TableSearchInput
          anchor={searchMenuAnchor}
          onClose={() => setSearchMenuAnchor(null)}
          useTableDispatch={useUserTableDispatch}
        />

        <TableFilterInput
          anchor={filterMenuAnchor}
          onClose={() => setFilterMenuAnchor(null)}
          keyOptions={tableColumns.filter((col) => !!config?.[col.id]?.filter).map((col) => col.id)}
          keyLabels={activeTableLabels}
          filters={filters}
          tableColumns={tableColumns}
          tableConfig={config}
          addFilter={addFilter}
          removeFilter={removeFilter}
          removeAllFilters={removeAllFilters}
          filtersToRemove={filtersToRemove}
          setFiltersToRemove={setFiltersToRemove}
          getOptionsFormSelectFieldConfig={getOptionsFromSelectFieldConfig}
        />

        <TableSettingsMenu
          anchor={settingsAnchor}
          onClose={() => setSettingsAnchor(null)}
          keyLabels={activeTableLabels}
          keyOptions={tableColumns.map((col) => col.id)}
          hiddenColumns={hiddenColumns}
          addHiddenColumn={addHiddenColumn}
          removeHiddenColumn={removeHiddenColumn}
          removeOptions={[ID_KEY]}
        />

        <TableEditItemsPopover
          anchor={editUsersAnchor}
          onClose={() => setEditUsersAnchor(null)}
          keyLabels={activeTableLabels}
          keyOptions={activeTableColumns.map((col) => col.id)}
          config={config}
          tableColumns={activeTableColumns}
          editItems={editItems}
          formatForSave={formatUserForSave}
          itemLabel="Users"
          itemIds={selectedItems}
        />
      </Stack>
    </Box>
  );
};
